<template>
  <div class="menu-report">
    <ui-button-multi
      class="mr-4 ml-4 mt-6"
      :ignore-first="true"
      button-style="outline-accent"
      :menu-list="reportMultiMenuList"
      :planLimits="itemActions.drilldownHome.planLimits"
      v-if="isBasicReport"
    ></ui-button-multi>

    <ui-button-multi
      class="mr-4 ml-4 mt-6"
      :ignore-first="true"
      button-style="outline-alt"
      :menu-list="excludeMultiMenuList"
      v-if="isBasicReport"
    ></ui-button-multi>

    <ui-button-multi
      class="menu-layout__filter tutorial-first-step mr-4 ml-4 mt-6"
      :class="{
        'menu-layout__filter--hide': !store.report.showFilters,
      }"
      :menu-list="createItemListFiltered"
      :ignore-first="true"
      :ignore-rest="createItemListFiltered.length === 1"
      v-if="isBasicReport"
    ></ui-button-multi>

    <ui-button-multi
      v-if="isBasicReport && itemActions.edit.showCondition"
      class="menu-layout__filter mr-4 ml-4 mt-6"
      :class="{
        'menu-layout__filter--hide': !store.report.showFilters,
      }"
      :planLimits="itemActions.edit.planLimits"
      :ignore-first="true"
      :ignore-rest="!isReport()"
      :menu-list="editMultiMenuListFiltered"
    ></ui-button-multi>

    <ui-button
      v-if="!isReport() && itemMenuFiltered && itemMenuFiltered.length"
      class="menu-layout__filter mr-4 ml-4 mt-6"
      :class="{
        'menu-layout__filter--hide': !store.report.showFilters,
      }"
      :text="t('Actions')"
      icon="fas fa-fire"
      :disabled="!itemMenuFilteredAndEnabled || !itemMenuFilteredAndEnabled.length"
      :menu-props="{
        menuWidth: 0,
      }"
      :menu-list="itemMenuFiltered"
    ></ui-button>

    <ui-button
      v-if="isBasicReport && !isReport() && (is('landings') || is('offers'))"
      class="menu-layout__filter mr-4 ml-4 mt-6"
      :class="{ 'menu-layout__filter--hide': !store.report.showFilters }"
      :text="t('Import')"
      icon="fas fa-file-import"
      :planLimits="importPlanLimits"
      @click="() => dialogActionsEvents.common.import()"
    ></ui-button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n, useMenuReportActions, type MenuReportRowAction } from '@/composables';
import { useStore } from '@/store';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const {
  isReport,
  is,
  itemActions,
  itemMenu,
  dialogActionsEvents,
  computedType,
} = useMenuReportActions();

const notReports = ['conversions', 'postbacks', 'errors', 'live-visits', 'live-clicks'];

const isBasicReport = computed(() => !notReports.includes(computedType.value));

const filterByShowCondition = (items: MenuReportRowAction[]) =>
  items.filter((item) => typeof item.showCondition === 'undefined' || item.showCondition);

const reportMultiMenuList = computed(() =>
  isReport()
    ? [
        itemActions.value.drilldown,
        {
          ...itemActions.value.drilldownContinueNewTab,
          icon: null,
        },
        {
          ...itemActions.value.drilldownOpenInNewTab,
          icon: null as string,
        },
      ]
    : [itemActions.value.drilldownHome],
);

const excludeMultiMenuList = computed(() =>
  isReport()
    ? [
        itemActions.value.exclude,
        {
          ...itemActions.value.excludeContinueNewTab,
          icon: null,
        },
        {
          ...itemActions.value.excludeStartNewTab,
          icon: null,
        },
      ]
    : [itemActions.value.excludeHome],
);

const editMultiMenuList = computed(() => [
  itemActions.value.edit,
  itemActions.value.editDrilldown,
]);

const editMultiMenuListFiltered = computed(() => filterByShowCondition(editMultiMenuList.value));

const createItemList = computed(() => [
  itemActions.value.create,
  itemActions.value.createFromTemplate,
  itemActions.value.createCampaign,
]);

const importPlanLimits = computed(() => {
  switch (route.params.type) {
    case 'offers':
      return 'importOffers';
    case 'landings':
      return 'importLandings';
  }
  return false;
});

const createItemListFiltered = computed(() => filterByShowCondition(createItemList.value));

const itemMenuFiltered = computed(() => filterByShowCondition(itemMenu.value));

const itemMenuFilteredAndEnabled = computed(
  () => itemMenuFiltered.value.filter((item) => !item.disabled),
);
</script>

<style lang="scss">
.menu-report {
  display: contents;

  &__context-menu-item {
    display: flex;
    align-items: baseline;
  }

  &__item-action {
    display: flex;
    flex-wrap: wrap;

    &--show-anyway {
      display: flex !important;
    }
  }

  &__dropdown-item-action {
    &--disabled {
      .v-list__tile,
      .v-icon {
        color: var(--color-gray20) !important;
      }
    }
  }

  &__btn-drop.v-btn {
    .v-btn__content {
      display: flex;
      align-items: center;
    }
  }

  & &__btn-drop &__btn-drop-icon.v-icon {
    margin-right: 0px;
  }

  .v-btn__content {
    .v-icon {
      vertical-align: initial;
      line-height: 13px;
    }
  }

  @media all and (max-width: 600px) {
    .layout-btn-group & .btn-group__wrap .v-btn:first-child,
    .layout-btn-group & .btn-group__wrap .v-btn:last-child {
      border-radius: 0 !important;
    }

    .layout-btn-group & .btn-group__wrap .v-btn:not(:last-child) {
      border-right-color: var(--color-gray20);
      border-right-style: solid;
      border-right-width: 1px;
    }
  }
}
</style>
